import FormHeader from "@/components/booking/FormHeader.tsx";
import {useContext} from "react";
import {OscarContext, OscarContextType} from "@/components/context/OscarContext.tsx";
import {FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form.tsx";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group.tsx";
import {useFormContext} from "react-hook-form";
import {z} from "zod";
import {BookingFormSchema} from "@/components/booking/form/BookingFormSchema.ts";
import {Input} from "@/components/ui/input.tsx";
import {OscarConfigModel} from "@/lib/oscar/ApiModels.ts";
import {BookingKind, BookingKindHelper} from "@/lib/enum/BookingKind.ts";

export function BinForm() {
    const { appConfig, localConfig } = useContext(OscarContext) as OscarContextType
    const { control, watch, setValue, resetField } = useFormContext<z.infer<typeof BookingFormSchema>>();

    // Get the rules for the bin.
    const rules = findRules(appConfig, watch("binSize"), watch("binType"));
    const typeRules = findTypeRules(appConfig, watch("binType"));

    // Get the bin size.
    const binKind = watch("binKind");
    const binSize = watch("binSize");
    const binType = watch("binType");

    const onSizeChange = (size: string) => {
        if (binType && binType != "") {
            // Make sure the bin type is valid for the bin size.
            for (const type of appConfig?.bookingData?.binTypes ?? []) {
                if (type.id === binType && type.sizes != null && !type.sizes.includes(size)) {
                    // Invalid bin type for the bin size.
                    resetField("binType");
                    break;
                }
            }
        }
        setValue("binSize", size);
    }

    return (
        <div className={"os-app-row os-flex os-flex-col os-gap-4"}>
            <FormHeader>{BookingKindHelper.getBookingKindLabel(binKind)}</FormHeader>
            <FormField
                key={binSize || "binSize"}
                control={control}
                name="binSize"
                render={({field}) => (
                    <FormItem>
                        <FormLabel>What size {BookingKindHelper.getBookingKindSlug(binKind)} would you like?</FormLabel>
                        <FormControl>
                            <RadioGroup
                                onValueChange={onSizeChange}
                                defaultValue={field.value}
                            >
                                {(binKind == BookingKind.BIN ? appConfig?.bookingData?.binSizes : appConfig?.bookingData?.bagSizes)?.map((binSize) => (
                                    <FormItem
                                        key={binSize.id}
                                        className="os-flex os-flex-row os-items-center os-justify-center os-gap-2 os-flex-1 !os-space-y-0 os-py-2">
                                        <FormControl className={"os-brand-text"}>
                                            <RadioGroupItem value={binSize.id}/>
                                        </FormControl>
                                        <FormLabel className="font-normal">
                                            {binSize.name}
                                        </FormLabel>
                                    </FormItem>
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </FormItem>
                )}
            />

            <FormField
                key={binType || "binType"}
                control={control}
                name="binType"
                render={({field}) => (
                    <FormItem>
                        <FormLabel>What are you getting rid of?</FormLabel>
                        <FormControl>
                            <RadioGroup
                                className={localConfig.tightMode ? "os-grid os-grid-cols-1 md:os-grid-cols-3" : ""}
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                            >
                                {(binKind == BookingKind.BIN ? appConfig?.bookingData?.binTypes : appConfig?.bookingData?.bagTypes)?.map((binType) => {
                                    const disabled = !(!binSize || !binType.sizes || binType.sizes.includes(binSize));

                                    return (
                                        <FormItem
                                            key={binType.id}
                                            className="os-flex os-flex-row os-items-center os-justify-center os-gap-2 os-flex-1 !os-space-y-0 os-py-2">
                                            <FormControl className={"os-brand-text"}>
                                                <RadioGroupItem value={binType.id} disabled={disabled}/>
                                            </FormControl>
                                            <FormLabel className={"os-font-normal " + (disabled ? "os-opacity-50" : "")}>
                                                {binType.name}
                                            </FormLabel>
                                        </FormItem>
                                    );
                                })}
                            </RadioGroup>
                        </FormControl>
                    </FormItem>
                )}
            />

            {(rules || typeRules) && (
                <div className="os-brand-pill-outline os-flex os-flex-col os-gap-0.5">
                    <div className="os-font-bold os-brand-text os-mb-1">Booking Conditions</div>
                    {typeRules && (
                        <div className="os-text-sm">
                            {typeRules.message}
                        </div>
                    )}

                    {rules && (
                        <div className="os-text-sm">
                            {rules.message}
                        </div>
                    )}

                    <div className="os-text-xs os-text-gray-500 os-mt-1">
                        This is in addition to our terms and conditions & additional charges.
                    </div>
                </div>
            )}

            <FormLabel>Is there any special items going in the {BookingKindHelper.getBookingKindSlug(binKind)}?</FormLabel>
            <div className="os-grid os-grid-cols-1 md:os-flex md:os-flex-row os-justify-center os-gap-4">
                {appConfig?.bookingData?.extras.map((extra) => (
                    <FormField
                        key={extra.id}
                        control={control}
                        name={`extras.${extra.id}`}
                        render={({field}) => (
                            <FormItem className="os-flex-1">
                                <FormLabel>{extra.name}</FormLabel>
                                <FormControl>
                                    <Input type="number" min={0} required={false} placeholder='0' {...field} />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />
                ))}
            </div>

        </div>
    );
}

/// Find the rules for a bin type & size.
function findRules(appConfig: OscarConfigModel | null, binSize: string, binType: string) {
    if (!appConfig?.bookingData?.rules) {
        return null;
    }

    if (!binSize || !binType) {
        return null;
    }

    // Rule key
    const ruleKey = `${binType}-${binSize}`;
    return appConfig.bookingData.rules[ruleKey];
}

/// Find the booking type rules.
function findTypeRules(appConfig: OscarConfigModel | null, binType: string) {
    if (!appConfig?.bookingData?.rules) {
        return null;
    }

    if (!binType) {
        return null;
    }

    return appConfig.bookingData.rules[binType];
}